import SEO from '../components/SEO'
import DownloadCTA from '../components/sections/DownloadCTA'

const platforms = [
  // Video
  { name: 'Zoom', type: 'Video Call', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'Google Meet', type: 'Video Call', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'Microsoft Teams', type: 'Video Call', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'WebEx', type: 'Video Call', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'BlueJeans', type: 'Video Call', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'GoTo Meeting', type: 'Video Call', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  // Assessment
  { name: 'HireVue', type: 'Assessment', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'CodeSignal', type: 'Assessment', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'HackerRank', type: 'Assessment', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'CoderPad', type: 'Assessment', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'Codility', type: 'Assessment', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  { name: 'Karat', type: 'Assessment', audio: '✅', screenCapture: '✅', stealth: '✅', notes: '' },
  // Phone
  { name: 'Phone Call', type: 'Phone', audio: '✅', screenCapture: '—', stealth: '✅', notes: 'Mic capture only' },
  { name: 'Google Voice', type: 'Phone', audio: '✅', screenCapture: '—', stealth: '✅', notes: 'Mic capture only' },
  { name: 'WhatsApp Call', type: 'Phone', audio: '✅', screenCapture: '—', stealth: '✅', notes: 'Mic capture only' },
]

const osSupport = [
  { os: 'macOS 13+', audio: '✅ (ScreenCaptureKit)', screen: '✅ (ScreenCaptureKit)', stealth: '✅ (Content Protected)', notes: 'Full support' },
  { os: 'Windows 10/11', audio: '✅ (WASAPI Loopback)', screen: '✅ (xcap)', stealth: '✅ (WDA_EXCLUDEFROMCAPTURE)', notes: 'Full support' },
  { os: 'Linux (X11)', audio: '✅ (PulseAudio)', screen: '✅ (xcap)', stealth: '⚠️ (No OS API)', notes: 'Use "Share a tab" mode' },
  { os: 'Linux (Wayland)', audio: '✅ (PipeWire)', screen: '✅ (XDG Portal)', stealth: '⚠️ (No OS API)', notes: 'Use "Share a tab" mode' },
]

function CompatibilityPage() {
  return (
    <>
      <SEO
        title="Platform Compatibility"
        description="Check which interview platforms work with Intervio. Tested with Zoom, Google Meet, Teams, HireVue, CodeSignal, HackerRank, and more."
        path="/compatibility"
      />

      <section style={{ padding: '80px 0 40px', textAlign: 'center' }}>
        <div className="container">
          <h1 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: 16 }}>
            Platform Compatibility
          </h1>
          <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary, #8b8fa4)', maxWidth: 640, margin: '0 auto' }}>
            Intervio works with every major interview platform. Here's the full compatibility matrix.
          </p>
        </div>
      </section>

      {/* Interview Platforms */}
      <section style={{ padding: '40px 0' }}>
        <div className="container" style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 24 }}>
            Interview Platforms
          </h2>
          <div style={{
            border: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))',
            borderRadius: 12,
            overflow: 'auto',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 600 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary, rgba(22, 25, 34, 0.7))' }}>
                  {['Platform', 'Type', 'Audio Capture', 'Screen Capture', 'Stealth', 'Notes'].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Platform' || h === 'Notes' ? 'left' : 'center', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #8b8fa4)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {platforms.map((p, i) => (
                  <tr key={p.name} style={{ borderTop: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))', background: i % 2 === 0 ? 'transparent' : 'var(--bg-tertiary, rgba(28, 31, 46, 0.15))' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{p.name}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center', color: 'var(--text-secondary, #8b8fa4)', fontSize: 12 }}>{p.type}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>{p.audio}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>{p.screenCapture}</td>
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>{p.stealth}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted, #5c6078)' }}>{p.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* OS Support */}
      <section style={{ padding: '40px 0 60px' }}>
        <div className="container" style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 24 }}>
            Operating System Support
          </h2>
          <div style={{
            border: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))',
            borderRadius: 12,
            overflow: 'auto',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 600 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary, rgba(22, 25, 34, 0.7))' }}>
                  {['OS', 'Audio Capture', 'Screen Capture', 'Stealth', 'Notes'].map((h) => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary, #8b8fa4)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {osSupport.map((o, i) => (
                  <tr key={o.os} style={{ borderTop: '1px solid var(--border-color, rgba(42, 46, 62, 0.6))', background: i % 2 === 0 ? 'transparent' : 'var(--bg-tertiary, rgba(28, 31, 46, 0.15))' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 500 }}>{o.os}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>{o.audio}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>{o.screen}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>{o.stealth}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted, #5c6078)' }}>{o.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <DownloadCTA />
    </>
  )
}

export default CompatibilityPage
